/**
 * Governance Guardrails Page
 *
 * Route: /guardrails | Nav: Operations > Guardrails
 *
 * Read-only overview of governance rules:
 * - Enforcement configuration
 * - Framework overrides
 * - Merge gate defaults
 * - Policy rules summary
 */
import React, { useEffect, useMemo } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  makeStyles,
  tokens,
  Spinner,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import {
  ShieldCheckmarkRegular,
  EditRegular,
  ShieldRegular,
  CheckmarkCircleRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { enforcementApi, mergeGateApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import type {
  FrameworkOverride,
} from '../types';
import { useNavigate } from 'react-router-dom';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';
import {
  ListPageLayout,
  ListToolbar,
  Pager,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

/**
 * Reference migration onto the List & Grid Standard — see
 * `.github/context/list-grid-standard.context.md` and
 * `.github/context/list-grid-standard-behaviours.context.md`.
 *
 * This grid is read-only display today (this page's docstring says "Read-only overview of
 * governance rules") — the per-row `enforcementApi.updateFrameworkOverride` mutation is wired
 * up on `MergeGateSettingsPage`, not here. No mutation on THIS page → Actions column omitted (§18).
 */

const useStyles = makeStyles({
  overridesBody: {
    padding: tokens.spacingHorizontalM,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    gap: tokens.spacingVerticalM,
  },
});

/** Wraps the already-fetched (unpaged) `frameworkOverrides` array as a `PagedResult` — no extra
 *  network call, since the data is owned by the page-level `enforcementConfig` query.
 *  `useAdaptiveGridData` applies search/sort/paging in memory (client mode). */
async function fetchFrameworkOverridesPage(
  overrides: FrameworkOverride[],
  params: ListQueryParams
): Promise<PagedResult<FrameworkOverride>> {
  return {
    items: overrides,
    totalCount: overrides.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getActionColor = (
  action: string
): 'success' | 'warning' | 'danger' => {
  switch (action) {
    case 'Allow':
      return 'success';
    case 'Warn':
      return 'warning';
    case 'Block':
      return 'danger';
    default:
      return 'warning';
  }
};

// ---------------------------------------------------------------------------
// GuardrailsPage
// ---------------------------------------------------------------------------

export const GuardrailsPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const navigate = useNavigate();

  // Queries — retry: 1 so the page doesn't spin forever when endpoints return 404
  const {
    data: enforcementConfig,
    isLoading: configLoading,
    isFetching: configFetching,
    isError: configError,
    error: configErr,
    refetch: refetchConfig,
  } = useQuery({
    queryKey: ['enforcementConfig'],
    queryFn: () => enforcementApi.getConfig(),
    retry: 1,
  });

  const { data: mergeGatePolicies, isLoading: policiesLoading, isError: policiesError, error: policiesErr } = useQuery({
    queryKey: ['mergeGatePolicies'],
    queryFn: () => mergeGateApi.getPolicies(),
    retry: 1,
  });

  const isLoading = configLoading || policiesLoading;
  const allFailed = !isLoading && configError && policiesError;

  // Derived merge gate stats
  const mergeGateStats = useMemo(() => {
    if (!mergeGatePolicies || mergeGatePolicies.length === 0) {
      return { total: 0, active: 0, blockOnCriticalPct: 0, blockOnHighPct: 0 };
    }
    const active = mergeGatePolicies.filter((p) => p.isEnabled);
    const blockOnCritical = active.filter((p) => p.blockOnCritical).length;
    const blockOnHigh = active.filter((p) => p.blockOnHigh).length;
    return {
      total: mergeGatePolicies.length,
      active: active.length,
      blockOnCriticalPct: active.length > 0 ? Math.round((blockOnCritical / active.length) * 100) : 0,
      blockOnHighPct: active.length > 0 ? Math.round((blockOnHigh / active.length) * 100) : 0,
    };
  }, [mergeGatePolicies]);

  // Framework Overrides list — List & Grid Standard wiring (URL state, adaptive paging, sort).
  const frameworkOverrides = useMemo(
    () => enforcementConfig?.frameworkOverrides ?? [],
    [enforcementConfig]
  );

  const { state: overridesState, setPage: setOverridesPage, setSort: setOverridesSort, setSearch: setOverridesSearch } =
    useListUrlState({ defaultSort: 'frameworkName:asc' });

  const [overridesSortField, overridesSortDirRaw] = overridesState.sort!.split(':');
  const overridesSortDir: SortDirection = overridesSortDirRaw === 'desc' ? 'desc' : 'asc';
  const overridesSortState: SortState[] = [{ field: overridesSortField, dir: overridesSortDir }];

  const toggleOverridesSort = (field: string) => {
    if (field === overridesSortField) {
      setOverridesSort(`${field}:${overridesSortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setOverridesSort(`${field}:asc`);
    }
  };

  const overridesSortIcon = (field: string) => {
    if (field !== overridesSortField) return <ArrowSort16Regular />;
    return overridesSortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const overridesGrid = useAdaptiveGridData<FrameworkOverride>({
    queryKeyBase: ['framework-overrides', frameworkOverrides],
    fetchPage: (params) => fetchFrameworkOverridesPage(frameworkOverrides, params),
    page: overridesState.page,
    pageSize: overridesState.pageSize,
    search: overridesState.search,
    searchFields: ['frameworkName'],
    sortState: overridesSortState,
    enabled: !!enforcementConfig,
  });

  const {
    items: overrideItems,
    totalCount: overridesTotalCount,
    totalPages: overridesTotalPages,
    hasPreviousPage: overridesHasPreviousPage,
    hasNextPage: overridesHasNextPage,
    isLoading: overridesLoading,
    isFetching: overridesFetching,
  } = overridesGrid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after search/refresh.
  useEffect(() => {
    if (!overridesLoading && overridesState.page > overridesTotalPages) {
      setOverridesPage(overridesTotalPages);
    }
  }, [overridesLoading, overridesState.page, overridesTotalPages, setOverridesPage]);

  let overridesBody: React.ReactNode;
  if (overridesLoading) {
    overridesBody = <LoadingSkeleton rows={4} columns={3} />;
  } else if (configError) {
    overridesBody = (
      <ErrorState message="Unable to load framework overrides." onRetry={refetchConfig} />
    );
  } else if (frameworkOverrides.length === 0) {
    overridesBody = (
      <EmptyState
        icon={<ShieldCheckmarkRegular />}
        title="No Framework Overrides"
        description="No framework-specific overrides configured."
      />
    );
  } else {
    overridesBody = (
      <ListLoadingOverlay busy={overridesFetching && !overridesLoading}>
        <Table aria-label="Framework overrides">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={
                  overridesSortField === 'frameworkName'
                    ? overridesSortDir === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
                sortIcon={overridesSortIcon('frameworkName')}
                onClick={() => toggleOverridesSort('frameworkName')}
                style={{ width: '100%' }}
              >
                Framework
              </TableHeaderCell>
              <TableHeaderCell>Action</TableHeaderCell>
              <TableHeaderCell>Enabled</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {overrideItems.map((item) => (
              <TableRow key={item.frameworkCode}>
                <TableCell>
                  <Body1 style={{ fontWeight: 500 }}>{item.frameworkName}</Body1>
                </TableCell>
                <TableCell>
                  <Badge appearance="filled" color={getActionColor(item.action)}>
                    {item.action}
                  </Badge>
                </TableCell>
                <TableCell>
                  {item.isEnabled ? (
                    <Badge appearance="filled" color="success">
                      Enabled
                    </Badge>
                  ) : (
                    <Badge appearance="filled" color="subtle">
                      Disabled
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: tokens.spacingVerticalXXXL,
        }}
      >
        <Spinner size="large" label="Loading governance guardrails..." />
      </div>
    );
  }

  // Auth error detection
  const queryError = (configErr || policiesErr) as any;
  const isUnauth = queryError?.status === 401 || queryError?.message?.includes('MSAL');
  if (isUnauth) {
    return (
      <div className={ps.page}>
        <PageHeader title="Governance Guardrails" subtitle="Policy definitions and guardrails governing your delivery pipelines" />
        <UnauthenticatedBanner feature="guardrails" />
      </div>
    );
  }

  // Error / empty state when API endpoints aren't available yet
  if (allFailed) {
    return (
      <div className={ps.page}>
        <PageHeader title="Governance Guardrails" subtitle="Policy definitions and guardrails governing your delivery pipelines" />
        <Card>
          <div style={{ padding: tokens.spacingVerticalXXL, textAlign: 'center' as const }}>
            <ShieldCheckmarkRegular
              style={{
                fontSize: '3rem',
                color: tokens.colorNeutralForeground3,
                display: 'block',
                margin: '0 auto',
                marginBottom: tokens.spacingVerticalM,
              }}
            />
            <Title2 block align="center" style={{ marginBottom: tokens.spacingVerticalS }}>No Guardrails Configured</Title2>
            <Body1 block align="center" style={{ color: tokens.colorNeutralForeground3 }}>
              Governance guardrails activate when enforcement policies are configured. Add a policy below to get started.
            </Body1>
            <div style={{ marginTop: tokens.spacingVerticalL }}>
              <Button appearance="primary" onClick={() => navigate('/compliance')}>
                Go to Compliance
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={ps.page}>
      <PageHeader
        title="Policies"
        subtitle="Policy definitions and enforcement rules governing your delivery pipelines"
        actions={
          <Button
            appearance="subtle"
            icon={<EditRegular />}
            onClick={() => navigate('/compliance')}
          >
            Edit Settings
          </Button>
        }
      />

      {/* Guardrails Summary — card-style operational indicators */}
      <div className={ps.metricGrid}>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <ShieldCheckmarkRegular
                style={{
                  fontSize: '1.5rem',
                  color: enforcementConfig?.isEnabled
                    ? tokens.colorPaletteGreenForeground1
                    : tokens.colorNeutralForeground3,
                }}
              />
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Enforcement</Body2>
              <Title2
                style={{
                  color: enforcementConfig?.isEnabled
                    ? tokens.colorPaletteGreenForeground1
                    : tokens.colorNeutralForeground3,
                }}
              >
                {enforcementConfig?.isEnabled ? 'Active' : 'Inactive'}
              </Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Default Action</Body2>
              <Badge
                appearance="filled"
                color={getActionColor(enforcementConfig?.defaultAction || 'Warn')}
                size="large"
              >
                {enforcementConfig?.defaultAction || 'Not configured'}
              </Badge>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Strict Mode</Body2>
              <Badge
                appearance="filled"
                color={enforcementConfig?.strictMode ? 'danger' : 'subtle'}
                size="large"
              >
                {enforcementConfig?.strictMode ? 'ENABLED' : 'DISABLED'}
              </Badge>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <ShieldRegular
                style={{
                  fontSize: '1.5rem',
                  color: mergeGateStats.active > 0
                    ? tokens.colorPaletteGreenForeground1
                    : tokens.colorNeutralForeground3,
                }}
              />
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Merge Gate</Body2>
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                <Badge
                  appearance="filled"
                  color={mergeGateStats.active > 0 ? 'success' : 'subtle'}
                  size="large"
                >
                  {mergeGateStats.active > 0 ? 'ACTIVE' : 'INACTIVE'}
                </Badge>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Delivery Protection Indicator */}
      {mergeGateStats.active > 0 && (
        <Card>
          <div className={ps.cardBody}>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
              <CheckmarkCircleRegular
                style={{ fontSize: '1.5rem', color: tokens.colorPaletteGreenForeground1 }}
              />
              <div style={{ flex: 1 }}>
                <Body1 style={{ fontWeight: 600 }}>Delivery Protection Active</Body1>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                  {mergeGateStats.active} of {mergeGateStats.total} merge gate policies are actively protecting your delivery pipelines.
                  {mergeGateStats.blockOnCriticalPct > 0 && ` ${mergeGateStats.blockOnCriticalPct}% block on critical findings.`}
                </Body2>
              </div>
              <Badge appearance="filled" color="success">Protected</Badge>
            </div>
          </div>
        </Card>
      )}

      {/* Two column: Enforcement details + Merge Gate stats */}
      <div className={ps.twoColumn}>
        {/* Enforcement Configuration */}
        <Card>
          <CardHeader
            header={<Title2>Enforcement Rules</Title2>}
            description="How violations are handled"
          />
          <div className={ps.cardBody}>
            <div className={ps.rowList}>
              <div className={ps.rowItem}>
                <Body1>Default action</Body1>
                <Badge
                  appearance="filled"
                  color={getActionColor(enforcementConfig?.defaultAction || 'Warn')}
                >
                  {enforcementConfig?.defaultAction || 'Not configured'}
                </Badge>
              </div>
              <div className={ps.rowItem}>
                <Body1>Minimum severity</Body1>
                <Badge appearance="outline">
                  {enforcementConfig?.minimumSeverity || 'Not configured'}
                </Badge>
              </div>
              <div className={ps.rowItem}>
                <Body1>Strict mode</Body1>
                <Badge
                  appearance="filled"
                  color={enforcementConfig?.strictMode ? 'danger' : 'subtle'}
                >
                  {enforcementConfig?.strictMode ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div className={ps.rowItem}>
                <Body1>Framework overrides</Body1>
                <Body2>{enforcementConfig?.frameworkOverrides?.length ?? 0} configured</Body2>
              </div>
            </div>
          </div>
        </Card>

        {/* Merge Gate Defaults */}
        <Card>
          <CardHeader
            header={<Title2>Merge Gate Defaults</Title2>}
            description="PR blocking thresholds"
            action={
              <Button
                appearance="subtle"
                icon={<EditRegular />}
                size="small"
                onClick={() => navigate('/settings/merge-gate')}
              >
                Edit
              </Button>
            }
          />
          <div className={ps.cardBody}>
            <div className={ps.rowList}>
              <div className={ps.rowItem}>
                <Body1>Active policies</Body1>
                <Body2 style={{ fontWeight: 600 }}>
                  {mergeGateStats.active} of {mergeGateStats.total}
                </Body2>
              </div>
              <div className={ps.rowItem}>
                <Body1>Block on Critical</Body1>
                <Badge
                  appearance="filled"
                  color={mergeGateStats.blockOnCriticalPct > 0 ? 'danger' : 'subtle'}
                >
                  {mergeGateStats.blockOnCriticalPct}%
                </Badge>
              </div>
              <div className={ps.rowItem}>
                <Body1>Block on High</Body1>
                <Badge
                  appearance="filled"
                  color={mergeGateStats.blockOnHighPct > 0 ? 'warning' : 'subtle'}
                >
                  {mergeGateStats.blockOnHighPct}%
                </Badge>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Framework Overrides — List & Grid Standard. Header omitted from ListPageLayout: the
          Card's CardHeader already serves as this embedded section's title (this isn't a
          standalone list page like ApiKeysPage, which uses MasterDetailHeader for crumbs). */}
      <Card>
        <CardHeader
          header={<Title2>Framework Overrides</Title2>}
          description="Per-framework enforcement action overrides"
        />
        <div className={styles.overridesBody}>
          <ListPageLayout
            toolbar={
              <ListToolbar
                search={overridesState.search}
                onSearchChange={setOverridesSearch}
                searchPlaceholder="Search frameworks…"
                onRefresh={refetchConfig}
                isRefreshing={configFetching}
                testIdPrefix="framework-overrides"
              />
            }
            footer={
              <Pager
                page={overridesState.page}
                totalPages={overridesTotalPages}
                hasPreviousPage={overridesHasPreviousPage}
                hasNextPage={overridesHasNextPage}
                totalCount={overridesTotalCount}
                onPageChange={setOverridesPage}
                testId="framework-overrides-pager"
              />
            }
          >
            {overridesBody}
          </ListPageLayout>
        </div>
      </Card>
    </div>
  );
};
